// NEW_FILE_JUSTIFIED: Hook for claim draft save/update/load — keeps NewClaimPage under 200 lines
import { useState, useCallback, useEffect } from 'react';
import { useMutation, useQuery } from 'convex/react';
import { useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { api } from '../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: hooks in src/hooks/ need two levels up
import type { Id } from '../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: hooks in src/hooks/
import type { IClaimFormState } from '@/hooks/useClaimForm';
import { toast } from 'sonner';

function tsToDateStr(ts: number | undefined): string {
  if (!ts) return '';
  return format(new Date(ts), 'yyyy-MM-dd');
}

export function useClaimDraft(
  state: IClaimFormState,
  effectiveOrgId: Id<'organisations'> | null,
  dispatch: React.Dispatch<{
    type: 'LOAD_DRAFT';
    draft: Partial<IClaimFormState> & { policyId: string; currentStep: number };
  }>
) {
  const [searchParams] = useSearchParams();
  const draftIdParam = searchParams.get('draftId');

  const [draftClaimId, setDraftClaimId] = useState<Id<'claims'> | null>(
    draftIdParam ? (draftIdParam as Id<'claims'>) : null // AS_JUSTIFIED: draftId URL param comes from MyClaimsListPage navigation with valid claim ID
  );
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [draftLoaded, setDraftLoaded] = useState(false);

  const saveDraftMutation = useMutation(api.insurance.claims.saveDraft);
  const updateDraftMutation = useMutation(api.insurance.claims.updateDraft);

  // Load draft data when resuming
  const draftData = useQuery(
    api.insurance.claims.get,
    draftClaimId && !draftLoaded ? { claimId: draftClaimId } : 'skip'
  );

  // Hydrate form from loaded draft
  useEffect(() => {
    if (!draftData || draftLoaded) return;
    if (draftData.status !== 'draft') return;

    dispatch({
      type: 'LOAD_DRAFT',
      draft: {
        currentStep: draftData.currentStep ?? 1,
        policyId: draftData.policyId,
        treatmentType: draftData.treatmentType ?? '',
        dateOfService: tsToDateStr(draftData.dateOfService),
        dateOfInjuryOrIllness: tsToDateStr(draftData.dateOfInjuryOrIllness),
        diagnosis: draftData.diagnosis ?? '',
        symptoms: draftData.symptoms ?? '',
        providerName: draftData.providerName ?? '',
        providerAddress: draftData.providerAddress ?? '',
        providerPhone: draftData.providerPhone ?? '',
        providerEmail: draftData.providerEmail ?? '',
        bankName: draftData.bankName ?? '',
        bankAccountHolder: draftData.bankAccountHolder ?? '',
        bankIban: draftData.bankIban ?? '',
        bankSwift: draftData.bankSwift ?? '',
        reimburseTarget: draftData.reimburseTarget ?? 'insured',
        customFieldValues: (draftData.customFieldValues ?? {}) as Record<string, string>, // AS_JUSTIFIED: customFieldValues stored as dynamic key-value object, matching form state shape
      },
    });
    setDraftLoaded(true);
  }, [draftData, draftLoaded, dispatch]);

  const buildDraftFields = useCallback(() => {
    const dateOfService = state.dateOfService ? new Date(state.dateOfService).getTime() : undefined;
    const dateOfInjuryOrIllness = state.dateOfInjuryOrIllness
      ? new Date(state.dateOfInjuryOrIllness).getTime()
      : undefined;
    const totalAmountClaimed = state.lineItems.reduce(
      (sum, li) => sum + (parseFloat(li.amount) || 0),
      0
    );
    const currency = state.lineItems[0]?.currency || 'EUR';

    return {
      treatmentType: state.treatmentType || undefined,
      dateOfService: dateOfService && !isNaN(dateOfService) ? dateOfService : undefined,
      dateOfInjuryOrIllness:
        dateOfInjuryOrIllness && !isNaN(dateOfInjuryOrIllness) ? dateOfInjuryOrIllness : undefined,
      diagnosis: state.diagnosis || undefined,
      symptoms: state.symptoms || undefined,
      providerName: state.providerName || undefined,
      providerAddress: state.providerAddress || undefined,
      providerPhone: state.providerPhone || undefined,
      providerEmail: state.providerEmail || undefined,
      totalAmountClaimed: totalAmountClaimed > 0 ? totalAmountClaimed : undefined,
      currency: currency || undefined,
      bankName: state.bankName || undefined,
      bankAccountHolder: state.bankAccountHolder || undefined,
      bankIban: state.bankIban || undefined,
      bankSwift: state.bankSwift || undefined,
      reimburseTarget:
        state.reimburseTarget === 'insured' || state.reimburseTarget === 'provider'
          ? state.reimburseTarget
          : undefined,
      customFieldValues:
        Object.keys(state.customFieldValues).length > 0 ? state.customFieldValues : undefined,
      currentStep: state.currentStep,
    };
  }, [state]);

  const handleSaveDraft = useCallback(async () => {
    if (!state.policyId) {
      toast.error('Please select a policy first');
      return;
    }
    if (!effectiveOrgId) {
      toast.error('Cannot determine organisation');
      return;
    }

    setIsSavingDraft(true);
    try {
      const fields = buildDraftFields();

      if (draftClaimId) {
        await updateDraftMutation({ claimId: draftClaimId, ...fields });
        toast.success('Draft updated');
      } else {
        const newDraftId = await saveDraftMutation({
          orgId: effectiveOrgId,
          policyId: state.policyId as Id<'insurancePolicies'>, // AS_JUSTIFIED: policyId selected from active policy list
          ...fields,
        });
        setDraftClaimId(newDraftId);
        toast.success('Draft saved');
      }
      // Stay on current step — no navigation
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save draft';
      toast.error(message);
    } finally {
      setIsSavingDraft(false);
    }
  }, [
    state.policyId,
    effectiveOrgId,
    draftClaimId,
    buildDraftFields,
    saveDraftMutation,
    updateDraftMutation,
  ]);

  return { draftClaimId, isSavingDraft, handleSaveDraft, draftLoaded };
}
